import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { PlatformSelector } from './PlatformSelector';
import { SchedulePicker } from './SchedulePicker';
import { ComposerModeToggle, ComposerMode } from './ComposerModeToggle';
import { PlatformContentEditor } from './PlatformContentEditor';
import {
  AdaptedPlatformContent,
  SocialMediaAccountPublic,
  SocialMediaPost,
  SocialPlatform,
} from '@/lib/api';
import { Loader2, Wand2 } from 'lucide-react';
import { ALL_PLATFORMS } from './platform-constants';

interface PostComposerProps {
  post: SocialMediaPost | null;
  content: string;
  setContent: (content: string) => void;
  targetAccountIds: string[];
  setTargetAccountIds: (ids: string[]) => void;
  accounts: SocialMediaAccountPublic[];
  isSaving: boolean;
  error: string | null;
  onSaveDraft: () => void;
  onAdapt: (platforms?: string[]) => void;
  onSchedule: (publishAt: string) => void;
  onPublishNow: () => void;
  onSavePlatformEdits: (content: Record<string, AdaptedPlatformContent>) => Promise<void>;
  isAdapting: boolean;
}

export function PostComposer({
  post,
  content,
  setContent,
  targetAccountIds,
  setTargetAccountIds,
  accounts,
  isSaving,
  error,
  onSaveDraft,
  onAdapt,
  onSchedule,
  onPublishNow,
  onSavePlatformEdits,
  isAdapting,
}: PostComposerProps) {
  const [mode, setMode] = useState<ComposerMode>('manual');
  const [showScheduler, setShowScheduler] = useState(false);
  const [approvedPlatforms, setApprovedPlatforms] = useState<Record<string, boolean>>({});
  const [editedPlatformText, setEditedPlatformText] = useState<Record<string, string>>({});

  // Reset approval/edit state whenever fresh AI-generated content arrives
  useEffect(() => {
    if (!post?.platformContent) return;
    const freshText: Record<string, string> = {};
    const freshApproved: Record<string, boolean> = {};
    Object.entries(post.platformContent).forEach(([platform, c]) => {
      freshText[platform] =
        c.hashtags.length > 0 ? `${c.text} ${c.hashtags.map((h) => `#${h}`).join(' ')}` : c.text;
      freshApproved[platform] = false;
    });
    setEditedPlatformText(freshText);
    setApprovedPlatforms(freshApproved);
  }, [post?.platformContent]);

  const toggleAccount = (accountId: string) => {
    setTargetAccountIds(
      targetAccountIds.includes(accountId)
        ? targetAccountIds.filter((id) => id !== accountId)
        : [...targetAccountIds, accountId],
    );
  };

  const activeAccounts = accounts.filter((a) => a.status === 'active');
  const hasNoActiveAccounts = activeAccounts.length === 0;

  // Platforms to generate content for — from selected accounts, or all platforms if none selected
  const selectedPlatforms = Array.from(
    new Set(
      accounts
        .filter((a) => targetAccountIds.includes(a.accountId))
        .map((a) => a.platform as SocialPlatform),
    ),
  );
  const platformsToGenerate = selectedPlatforms.length > 0 ? selectedPlatforms : ALL_PLATFORMS;

  const generatedPlatforms = Object.keys(post?.platformContent ?? {}) as SocialPlatform[];
  const allApproved =
    generatedPlatforms.length > 0 && generatedPlatforms.every((p) => approvedPlatforms[p]);

  const canPublish = !hasNoActiveAccounts && !!content.trim();
  const publishDisabledTitle = hasNoActiveAccounts
    ? 'Connect an account to publish'
    : !content.trim()
      ? 'Write something first'
      : undefined;

  const handleGenerate = () => {
    onAdapt(platformsToGenerate);
  };

  const handleSchedule = (publishAt: string) => {
    onSchedule(publishAt);
    setShowScheduler(false);
  };

  // Build updated AdaptedPlatformContent from the user's edits before publishing/scheduling
  const buildUpdatedPlatformContent = (): Record<string, AdaptedPlatformContent> => {
    const result: Record<string, AdaptedPlatformContent> = {};
    for (const platform of generatedPlatforms) {
      const original = post!.platformContent[platform];
      const editedText = editedPlatformText[platform] ?? original.text;
      result[platform] = {
        ...original,
        text: editedText,
        hashtags: [],
        characterCount: editedText.length,
      };
    }
    return result;
  };

  const handlePlatformPublish = async () => {
    await onSavePlatformEdits(buildUpdatedPlatformContent());
    onPublishNow();
  };

  const handlePlatformSchedule = async (publishAt: string) => {
    await onSavePlatformEdits(buildUpdatedPlatformContent());
    handleSchedule(publishAt);
  };

  const publishActionButtons = (disabled: boolean, extraTitle?: string) => (
    <div className="flex flex-wrap items-center justify-center gap-2">
      <Button
        onClick={onSaveDraft}
        disabled={isSaving || !content.trim() || disabled}
        size="sm"
        variant="outline"
      >
        {isSaving && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
        Save Draft
      </Button>
      <Button
        onClick={onPublishNow}
        disabled={!canPublish || disabled}
        size="sm"
        title={extraTitle ?? publishDisabledTitle}
      >
        Publish Now
      </Button>
      <Button
        variant="outline"
        size="sm"
        disabled={disabled || hasNoActiveAccounts}
        onClick={() => setShowScheduler((s) => !s)}
      >
        {showScheduler ? 'Cancel' : 'Schedule Post'}
      </Button>
    </div>
  );

  return (
    <div className="space-y-5">
      {error && (
        <div className="p-3 rounded-md bg-destructive/10 text-destructive text-sm">{error}</div>
      )}

      {/* 1. Target Account Selection */}
      <div className="space-y-2">
        <p className="text-sm font-medium">Post to</p>
        {accounts.length === 0 ? (
          <div className="p-3 rounded-md bg-muted text-sm text-muted-foreground">
            No connected accounts.{' '}
            <Link
              to="/a/arche_social_media/accounts"
              className="underline text-foreground hover:text-primary"
            >
              Connect one on the Accounts page
            </Link>{' '}
            before publishing.
          </div>
        ) : (
          <>
            <PlatformSelector
              accounts={accounts}
              selectedAccountIds={targetAccountIds}
              onToggle={toggleAccount}
            />
            {targetAccountIds.length === 0 && activeAccounts.length > 0 && (
              <p className="text-xs text-muted-foreground">
                No accounts selected — will publish to all active accounts.
              </p>
            )}
          </>
        )}
      </div>

      {/* 2. Mode Toggle */}
      <ComposerModeToggle mode={mode} onChange={setMode} />

      {/* 3. Text Area */}
      <div className="space-y-2">
        <Textarea
          placeholder="What's on your mind? Write your post here..."
          value={content}
          onChange={(e) => setContent(e.target.value)}
          rows={6}
          className="resize-none"
        />

        {/* 4. Contextual action buttons */}
        <div className="flex flex-col items-center gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-2">
          <span className="text-xs text-muted-foreground self-start sm:self-auto">
            {content.length} characters
          </span>

          {mode === 'manual' && publishActionButtons(false)}

          {mode === 'platform-specific' && (
            <Button
              onClick={handleGenerate}
              disabled={isAdapting || !content.trim()}
              size="sm"
              className="gap-1"
            >
              {isAdapting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Wand2 className="h-4 w-4" />
              )}
              Generate
            </Button>
          )}
        </div>
      </div>

      {/* 5. Inline Schedule Picker (Manual mode) */}
      {mode === 'manual' && showScheduler && (
        <SchedulePicker
          onSchedule={handleSchedule}
          onPublishNow={onPublishNow}
          isLoading={isSaving}
          hasActiveAccounts={!hasNoActiveAccounts}
        />
      )}

      {/* 6. Platform-specific generated content */}
      {mode === 'platform-specific' && generatedPlatforms.length > 0 && (
        <div className="space-y-3">
          <p className="text-sm font-medium">Review &amp; approve each platform post</p>
          {generatedPlatforms.map((platform) => (
            <PlatformContentEditor
              key={platform}
              platform={platform}
              content={post!.platformContent[platform]}
              editedText={editedPlatformText[platform] ?? ''}
              isApproved={approvedPlatforms[platform] ?? false}
              onChange={(p, text) => setEditedPlatformText((prev) => ({ ...prev, [p]: text }))}
              onApprove={(p, approved) =>
                setApprovedPlatforms((prev) => ({ ...prev, [p]: approved }))
              }
            />
          ))}
        </div>
      )}

      {/* 7. Final publish controls (Platform Specific mode, after generation) */}
      {mode === 'platform-specific' && generatedPlatforms.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs text-muted-foreground">
              {allApproved
                ? 'All platforms approved — ready to publish.'
                : `${generatedPlatforms.filter((p) => approvedPlatforms[p]).length}/${generatedPlatforms.length} platforms approved`}
            </span>
            <div className="flex items-center gap-2">
              <Button
                onClick={onSaveDraft}
                disabled={isSaving || !content.trim() || !allApproved}
                size="sm"
                variant="outline"
              >
                {isSaving && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
                Save Draft
              </Button>
              <Button
                onClick={handlePlatformPublish}
                disabled={!canPublish || !allApproved}
                size="sm"
                title={
                  !allApproved ? 'Approve all platforms before publishing' : publishDisabledTitle
                }
              >
                Publish Now
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={!allApproved || hasNoActiveAccounts}
                onClick={() => setShowScheduler((s) => !s)}
              >
                {showScheduler ? 'Cancel' : 'Schedule Post'}
              </Button>
            </div>
          </div>

          {showScheduler && allApproved && (
            <SchedulePicker
              onSchedule={handlePlatformSchedule}
              onPublishNow={handlePlatformPublish}
              isLoading={isSaving}
              hasActiveAccounts={!hasNoActiveAccounts}
            />
          )}
        </div>
      )}
    </div>
  );
}
